import React, { memo, useState, useEffect } from 'react';
import { ZoomableGroup, ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import chroma from 'chroma-js';
import api from '../../services/Api';

function getDarkenedColor(users) {
  const level = users / 500;
  return chroma('#0EA5E9').darken(level).hex().toString();
}

const COUNTRY_NAME_MAP = {
  'united states': 'united states of america',
};
function matchCountryName(countryName, geoProperties) {
  const normalizedCountryName = countryName.toLowerCase();
  const mappedCountryName = COUNTRY_NAME_MAP[normalizedCountryName] || normalizedCountryName;
  const geoNames = [
    geoProperties.NAME?.toLowerCase(),
    geoProperties.ADMIN?.toLowerCase(),
    geoProperties.name?.toLowerCase(),
  ];
  return geoNames.includes(mappedCountryName);
}

function findCountry(users, geoProperties) {
  return users.find(d => matchCountryName(d.name, geoProperties));
}

const MapChart = () => {
  const [content, setContent] = useState('');
  const [UserData, setUserData] = useState([]);
  const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/admin/user/users');
        const result = response.data;

        if (result.users && Array.isArray(result.users)) {
          const userCounts = result.users.reduce((acc, user) => {
            const country = user.country;
            if (country) {
              acc[country] = (acc[country] || 0) + 1;
            }
            return acc;
          }, {});

          const formattedData = Object.entries(userCounts).map(([name, users]) => ({
            name,
            users,
          }));

          setUserData(formattedData);
        } else {
          console.error('Unexpected API response structure:', result);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchData();
  }, []);

  const fillColor = geo => {
    const country = findCountry(UserData, geo.properties);
    return country ? getDarkenedColor(country.users) : '#9FBFF0';
  };

  const handleMouseEnter = geo => {
    const country = findCountry(UserData, geo.properties);
    setContent(
      country
        ? `${geo.properties.NAME || geo.properties.ADMIN || geo.properties.name}: ${
            country.users
          } users`
        : `${geo.properties.NAME || geo.properties.ADMIN || geo.properties.name}: No users`
    );
  };

  return (
    <div className="font-nohemi">
      <ComposableMap
        data-tooltip-id="my-tooltip"
        projection="geoMercator"
        projectionConfig={{ scale: 100 }}>
        <ZoomableGroup>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map(geo => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => handleMouseEnter(geo)}
                  onMouseLeave={() => setContent('')}
                  style={{
                    default: { fill: fillColor(geo), outline: 'none' },
                    hover: { fill: '#CFDFF7', outline: 'none' },
                    pressed: { fill: '#E42', outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      <ReactTooltip id="my-tooltip" content={content} place="top" float={true} />
    </div>
  );
};

export default memo(MapChart);
